import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { isSameDay, startOfDay, startOfYear, subMonths, subYears } from 'date-fns'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import { useNavigate } from 'react-router'
import { TradeRecord } from 'types/Instrument'
import { TradesGraphCustomDatesButton } from './CustomDatesButton'
import Skeleton from '@mui/material/Skeleton'
import { useQuery } from '../../../../hooks/useQuery'
import { calculatePriceChange } from '../../../issues/utils'
import { ColoredPercentage } from '../../../../components/entities/utils/ColoredPercentage'
import { CUSTOM_DATE_END_DATE_ROUTE_PARAM, CUSTOM_DATE_START_DATE_ROUTE_PARAM } from './CustomDatesForm'

export const TIME_SELECTOR_ROUTE_PARAM = 'graphStart'

export const mapTimeSelectorKeyToDate = (key: string) => {
    const searchParams = new URLSearchParams(location.search)
    const today = startOfDay(new Date()).getTime()
    const start = searchParams.get(CUSTOM_DATE_START_DATE_ROUTE_PARAM) ?? 0
    const end = searchParams.get(CUSTOM_DATE_END_DATE_ROUTE_PARAM) ?? today

    return (
        {
            '1m': { start: subMonths(new Date(), 1).getTime(), end: today },
            '3m': { start: subMonths(new Date(), 3).getTime(), end: today },
            startOfYear: { start: startOfYear(new Date()).getTime(), end: today },
            '1y': { start: subYears(new Date(), 1).getTime(), end: today },
            '3y': { start: subYears(new Date(), 3).getTime(), end: today },
            max: { start: 0, end: today },
            custom: {
                start: new Date(start).getTime(),
                end: new Date(end).getTime(),
            },
        }[key] || { start: 0, end: today }
    )
}

const possibleAlternatives: Array<{ key: string; labelKey: string; isDefault?: boolean }> = [
    { key: '1m', labelKey: 'oneMonth' },
    { key: '3m', labelKey: 'threeMonths' },
    { key: 'startOfYear', labelKey: 'yearToDate' },
    { key: '1y', labelKey: 'oneYear' },
    { key: '3y', labelKey: 'threeYears', isDefault: true },
    { key: 'max', labelKey: 'max' },
]

const getLastTradingRecord = (trades: TradeRecord[]) => {
    for (let i = trades.length - 1; i > 0; i--) {
        if (trades[i].closePrice !== null) return trades[i]
    }
}

export const TradesGraphTimeSelector = ({
    isLoading,
    shareTrades,
}: {
    isLoading: boolean
    shareTrades: TradeRecord[] | null
}) => {
    const { t } = useTranslation()
    const query = useQuery()
    const navigate = useNavigate()
    let firstTradingDate: number = 0
    let lastTradingDate: number = 0

    if (shareTrades) {
        const firstRecord = shareTrades.find((t) => t.closePrice !== null)
        const lastRecord = getLastTradingRecord(shareTrades)
        firstTradingDate = !firstRecord ? 0 : new Date(firstRecord.recordDate).getTime()
        lastTradingDate = !lastRecord ? 0 : new Date(lastRecord.recordDate).getTime()
    }

    const alternatives = possibleAlternatives
        .filter(
            ({ key }) =>
                isLoading ||
                mapTimeSelectorKeyToDate(key).start === 0 ||
                (mapTimeSelectorKeyToDate(key).start > firstTradingDate &&
                    mapTimeSelectorKeyToDate(key).start < lastTradingDate),
        )
        .map((alt) => {
            const label = t(`pages.companies.graph.timeRange.${alt.labelKey}`)

            if (isLoading) return { ...alt, label, priceChange: null }

            const startDate = mapTimeSelectorKeyToDate(alt.key).start
            const index = shareTrades?.findIndex(
                (t) =>
                    t.closePrice !== null &&
                    (isSameDay(t.recordDate, startDate) || new Date(t.recordDate).getTime() > startDate),
            )

            return {
                ...alt,
                label,
                priceChange:
                    !shareTrades || index === undefined
                        ? null
                        : calculatePriceChange(getLastTradingRecord(shareTrades), shareTrades[index].closePrice),
            }
        })

    const defaultAlternative = (
        alternatives.find(({ isDefault }) => isDefault) || alternatives[alternatives.length - 1]
    ).key
    const selectedAlternative =
        query.get(TIME_SELECTOR_ROUTE_PARAM) === 'custom'
            ? 'custom'
            : alternatives.find((a) => a.key === query.get(TIME_SELECTOR_ROUTE_PARAM))?.key

    useEffect(() => {
        const selectedAlt = query.get(TIME_SELECTOR_ROUTE_PARAM)
        if (!selectedAlt) {
            query.set(TIME_SELECTOR_ROUTE_PARAM, defaultAlternative)
            navigate(`?${query.toString()}`, { replace: true })
            return
        }

        if (shareTrades === null) return

        if (lastTradingDate < mapTimeSelectorKeyToDate(selectedAlt).start) {
            query.set(TIME_SELECTOR_ROUTE_PARAM, 'max')
            navigate(`?${query.toString()}`, { replace: true })
        }
    }, [defaultAlternative, lastTradingDate, navigate, query, shareTrades])

    const handleChangeAlternative = (key: string) => {
        query.set(TIME_SELECTOR_ROUTE_PARAM, key)
        navigate(`?${query.toString()}`, { replace: true })
    }

    return (
        <Tabs
            aria-label={t('pages.companies.graph.timeIntervalAria')}
            sx={{ '.MuiTabs-indicator': { backgroundColor: 'rgba(0, 0, 0, 0.85)' } }}
            variant='fullWidth'
            value={
                selectedAlternative === 'custom'
                    ? alternatives.length
                    : alternatives.findIndex((a) => a.key === (selectedAlternative ?? defaultAlternative))
            }
        >
            {alternatives.map((l) => (
                <Tab
                    key={l.label}
                    label={
                        <>
                            {l.label}
                            <br />
                            {isLoading && <Skeleton variant='rectangular' width={35} />}
                            {!isLoading && <ColoredPercentage value={l.priceChange} />}
                        </>
                    }
                    onClick={() => handleChangeAlternative(l.key)}
                    sx={{
                        flexBasis: 1,
                        flexGrow: possibleAlternatives.length >= alternatives.length - 1 ? 1 : 0,
                        minWidth: 60,
                        flexShrink: 1,
                        '&.Mui-selected': {
                            color: 'rgba(0, 0, 0, 0.85)',
                        },
                    }}
                />
            ))}

            {(isLoading || (firstTradingDate && lastTradingDate)) && (
                <TradesGraphCustomDatesButton
                    firstTradingDate={firstTradingDate}
                    isSelected={selectedAlternative === 'custom'}
                    lastTradingDate={lastTradingDate}
                />
            )}
        </Tabs>
    )
}
